import { Alert, Space } from 'antd';
import React, { useCallback } from 'react';
import { get, useController, useFormContext } from 'react-hook-form';

import { AcquisitionType } from '../types/types';
import { AcquisitionTypeCard } from './AcquisitionTypeCard';
import { AcquisitionTypeSelector } from './AcquisitionTypeSelector';

const ALL_TYPES: AcquisitionType[] = [
  AcquisitionType.Purchase,
  AcquisitionType.Subscription,
  AcquisitionType.Rental,
];

const acquisitionTypeKey: Record<AcquisitionType, string> = {
  [AcquisitionType.Purchase]: 'purchase',
  [AcquisitionType.Subscription]: 'subscription',
  [AcquisitionType.Rental]: 'rental',
};

interface AudienceConfigurationProps {
  audienceKey: 'subscriber' | 'freemium';
}

export const AudienceConfiguration: React.FC<AudienceConfigurationProps> = ({
  audienceKey,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { field } = useController({
    control,
    name: `configuration.${audienceKey}.acquisitionTypes`,
  });

  const activeTypes: AcquisitionType[] = field.value ?? [];
  const inactiveTypes = ALL_TYPES.filter(t => !activeTypes.includes(t));
  const acquisitionTypesError = get(
    errors,
    `configuration.${audienceKey}.acquisitionTypes`
  );

  const handleAdd = useCallback(
    (type: AcquisitionType) => {
      field.onChange([...activeTypes, type]);
    },
    [activeTypes, field]
  );

  const handleRemove = useCallback(
    (type: AcquisitionType) => {
      field.onChange(activeTypes.filter(t => t !== type));
    },
    [activeTypes, field]
  );

  return (
    <Space direction='vertical' size={12} style={{ width: '100%' }}>
      {activeTypes.map(type => (
        <AcquisitionTypeCard
          key={type}
          type={type}
          control={control}
          audienceKey={audienceKey}
          namePrefix={`configuration.${audienceKey}.${acquisitionTypeKey[type]}`}
          onRemove={handleRemove}
        />
      ))}

      <AcquisitionTypeSelector
        inactiveTypes={inactiveTypes}
        onAdd={handleAdd}
      />

      {acquisitionTypesError && (
        <Alert
          type='error'
          showIcon
          message={acquisitionTypesError.message}
          style={{ borderRadius: 6 }}
        />
      )}
    </Space>
  );
};
